import chunk from "chunk"

import type { IOrchestratedQuerySender } from "./orchestrated-query-interfaces"
import type { RpcMetricsCollector } from "./rpc-metrics"

/**
 * Base interface for all RPC execution strategies
 */
export interface IRpcStrategy {
  /**
   * Execute an RPC call with metrics collection
   * @param rpcCallFn - Function that performs the RPC call
   * @param metricsCollector - Optional metrics collector
   * @returns Result of the RPC call
   */
  execute<TResult>(rpcCallFn: () => Promise<TResult>, metricsCollector?: RpcMetricsCollector): Promise<TResult>
}

/**
 * Strategy for executing chunked RPC calls
 */
export interface IChunkedRpcStrategy extends IRpcStrategy {
  /**
   * Execute RPC calls in chunks
   * @param items - Items to process in chunks
   * @param query - Query instance to read chunk size from
   * @param defaultChunkSize - Default chunk size if query doesn't have one configured
   * @param rpcCallFn - Function to execute for each chunk
   * @param metricsCollector - Optional metrics collector
   * @returns Aggregated results from all chunks
   */
  executeChunked<TItem, TResult>(
    items: TItem[],
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number,
    rpcCallFn: (chunk: TItem[], chunkIndex: number) => Promise<TResult>,
    metricsCollector?: RpcMetricsCollector,
  ): Promise<TResult[]>
}

/**
 * Sequential chunked RPC strategy
 * Processes chunks one at a time, records metrics, handles errors
 */
export class SequentialChunkedRpcStrategy implements IChunkedRpcStrategy {
  /**
   * Execute a single RPC call with metrics
   */
  async execute<TResult>(rpcCallFn: () => Promise<TResult>, metricsCollector?: RpcMetricsCollector): Promise<TResult> {
    const startTime = Date.now()

    try {
      const result = await rpcCallFn()
      const durationMs = Date.now() - startTime

      // Record metrics for single call (chunkSize = 1, chunkIndex = 0)
      metricsCollector?.recordRpcCall(1, 0, result, true, undefined, durationMs)

      return result
    } catch (error) {
      const durationMs = Date.now() - startTime

      // Record failed metrics
      metricsCollector?.recordRpcCall(
        1,
        0,
        null,
        false,
        error instanceof Error ? error.message : String(error),
        durationMs,
      )

      throw error
    }
  }

  /**
   * Execute RPC calls in chunks
   */
  async executeChunked<TItem, TResult>(
    items: TItem[],
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number,
    rpcCallFn: (chunk: TItem[], chunkIndex: number) => Promise<TResult>,
    metricsCollector?: RpcMetricsCollector,
  ): Promise<TResult[]> {
    // Read chunk size from query (or use default)
    const chunkSize = query.getChunkSize() ?? defaultChunkSize

    // Split items into chunks
    const chunks = chunk(items, chunkSize)
    const results: TResult[] = []

    // Process chunks sequentially
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex]!
      const startTime = Date.now()

      try {
        const result = await rpcCallFn(chunk, chunkIndex)
        const durationMs = Date.now() - startTime

        // Record metrics for successful chunk
        metricsCollector?.recordRpcCall(chunkSize, chunkIndex, result, true, undefined, durationMs)

        results.push(result)
      } catch (error) {
        const durationMs = Date.now() - startTime

        // Record metrics for failed chunk
        metricsCollector?.recordRpcCall(
          chunkSize,
          chunkIndex,
          null,
          false,
          error instanceof Error ? error.message : String(error),
          durationMs,
        )

        // Re-throw to maintain existing error behavior
        throw error
      }
    }

    return results
  }
}

/**
 * Single RPC strategy for queries that make one RPC call (no chunking)
 * Still records metrics for profiling
 */
export class SingleRpcStrategy implements IRpcStrategy {
  /**
   * Execute a single RPC call with metrics collection
   */
  async execute<TResult>(rpcCallFn: () => Promise<TResult>, metricsCollector?: RpcMetricsCollector): Promise<TResult> {
    const startTime = Date.now()

    try {
      const result = await rpcCallFn()
      const durationMs = Date.now() - startTime

      // Record metrics for single call (chunkSize = 1, chunkIndex = 0)
      metricsCollector?.recordRpcCall(1, 0, result, true, undefined, durationMs)

      return result
    } catch (error) {
      const durationMs = Date.now() - startTime

      // Record failed metrics
      metricsCollector?.recordRpcCall(
        1,
        0,
        null,
        false,
        error instanceof Error ? error.message : String(error),
        durationMs,
      )

      throw error
    }
  }
}
